/**
 * NavigationRouteLayer - Displays the active navigation route on the map
 * Shows the route line with color based on air safety level
 * Includes destination marker and turn indicators
 */

import { useMemo } from 'react';
import { Source, Layer, Marker } from 'react-map-gl';
import { useNavigationStore } from '@/stores/useNavigationStore';
import { useLocationStore } from '@/stores';
import type { LayerProps } from 'react-map-gl';

/**
 * Convert route polyline to GeoJSON LineString
 */
function routeToGeoJSON(
  polyline: { lat: number; lng: number }[]
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: polyline.map((p) => [p.lng, p.lat]),
        },
      },
    ],
  };
}

/**
 * Find the index of the nearest polyline point to a given position.
 * Used to trim the route - only showing the portion ahead of the user.
 */
function findNearestPointIndex(
  position: { lat: number; lng: number },
  polyline: { lat: number; lng: number }[]
): number {
  let minDist = Infinity;
  let minIndex = 0;
  for (let i = 0; i < polyline.length; i++) {
    const dlat = position.lat - polyline[i].lat;
    const dlng = position.lng - polyline[i].lng;
    const dist = dlat * dlat + dlng * dlng; // squared distance is fine for comparison
    if (dist < minDist) {
      minDist = dist;
      minIndex = i;
    }
  }
  return minIndex;
}

/**
 * Get route color based on average Neural Index
 */
function getRouteColor(averageNI: number): { main: string; glow: string } {
  if (averageNI >= 80) {
    return { main: '#00ff88', glow: 'rgba(0, 255, 136, 0.4)' }; // Green - Safe
  }
  if (averageNI >= 60) {
    return { main: '#ffdd00', glow: 'rgba(255, 221, 0, 0.4)' }; // Yellow - Moderate
  }
  if (averageNI >= 40) {
    return { main: '#ffaa00', glow: 'rgba(255, 170, 0, 0.4)' }; // Orange - Caution
  }
  return { main: '#ff3366', glow: 'rgba(255, 51, 102, 0.4)' }; // Red - Danger
}

export function NavigationRouteLayer() {
  const activeRoute = useNavigationStore((state) => state.activeRoute);
  const isNavigating = useNavigationStore((state) => state.isNavigating);
  const currentStepIndex = useNavigationStore((state) => state.currentStepIndex);
  const isDemoMode = useNavigationStore((state) => state.isDemoMode);
  const currentPosition = useLocationStore((state) => state.currentPosition);

  // Trim polyline to only show remaining route ahead of user
  const remainingPolyline = useMemo(() => {
    if (!activeRoute?.polyline) return null;
    const polyline = activeRoute.polyline;

    // In demo mode, use step coordinates to estimate progress
    if (isDemoMode && currentStepIndex > 0 && currentStepIndex < activeRoute.steps.length) {
      const stepCoords = activeRoute.steps[currentStepIndex].coordinates;
      const idx = findNearestPointIndex(stepCoords, polyline);
      return idx > 0 ? polyline.slice(idx) : polyline;
    }

    // With real GPS, snap to nearest polyline point
    if (currentPosition) {
      const idx = findNearestPointIndex(currentPosition, polyline);
      return idx > 0 ? polyline.slice(idx) : polyline;
    }

    return polyline;
  }, [activeRoute?.polyline, activeRoute?.steps, currentPosition, currentStepIndex, isDemoMode]);

  // Convert trimmed polyline to GeoJSON
  const routeGeoJSON = useMemo(() => {
    if (!remainingPolyline?.length) return null;
    return routeToGeoJSON(remainingPolyline);
  }, [remainingPolyline]);

  // Get colors based on route safety
  const colors = useMemo(() => {
    if (!activeRoute) return { main: '#00ff88', glow: 'rgba(0, 255, 136, 0.4)' };
    return getRouteColor(activeRoute.averageNI);
  }, [activeRoute]);

  if (!isNavigating || !activeRoute || !routeGeoJSON) {
    return null;
  }

  // Route glow layer (wider, blurred for effect)
  const glowLayer: LayerProps = {
    id: 'navigation-route-glow',
    type: 'line',
    paint: {
      'line-color': colors.glow,
      'line-width': 28,
      'line-blur': 12,
    },
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
  };

  // Dark casing layer (makes route pop against any map background)
  const casingLayer: LayerProps = {
    id: 'navigation-route-casing',
    type: 'line',
    paint: {
      'line-color': '#000000',
      'line-width': 12,
      'line-opacity': 0.7,
    },
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
  };

  // Main route line
  const mainLineLayer: LayerProps = {
    id: 'navigation-route-main',
    type: 'line',
    paint: {
      'line-color': colors.main,
      'line-width': 8,
    },
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
  };

  // Animated dashed line (direction indicator)
  const dashLayer: LayerProps = {
    id: 'navigation-route-dash',
    type: 'line',
    paint: {
      'line-color': '#ffffff',
      'line-width': 2.5,
      'line-dasharray': [1, 2.5],
    },
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
  };

  return (
    <>
      {/* Route line */}
      <Source id="navigation-route" type="geojson" data={routeGeoJSON}>
        <Layer {...glowLayer} />
        <Layer {...casingLayer} />
        <Layer {...mainLineLayer} />
        <Layer {...dashLayer} />
      </Source>

      {/* Destination marker */}
      <Marker
        longitude={activeRoute.destination.lng}
        latitude={activeRoute.destination.lat}
        anchor="bottom"
      >
        <div className="relative">
          {/* Pulsing ring */}
          <div
            className="absolute -inset-3 rounded-full animate-ping"
            style={{ backgroundColor: colors.main, opacity: 0.3 }}
          />
          {/* Marker pin */}
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center shadow-lg"
            style={{ backgroundColor: colors.main }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="#000"
              className="w-5 h-5"
            >
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            </svg>
          </div>
          {/* Destination name */}
          <div
            className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1 rounded text-xs font-hud whitespace-nowrap"
            style={{ backgroundColor: 'rgba(0,0,0,0.8)', color: colors.main }}
          >
            {activeRoute.destinationName}
          </div>
        </div>
      </Marker>

      {/* Turn point markers */}
      {activeRoute.steps.slice(1, -1).map((step, index) => (
        <Marker
          key={index}
          longitude={step.coordinates.lng}
          latitude={step.coordinates.lat}
          anchor="center"
        >
          <div
            className="w-7 h-7 rounded-full border-2 flex items-center justify-center shadow-lg"
            style={{
              backgroundColor: 'rgba(0,0,0,0.85)',
              borderColor: colors.main,
              boxShadow: `0 0 8px ${colors.glow}`,
            }}
          >
            <TurnIcon maneuver={step.maneuver} color={colors.main} />
          </div>
        </Marker>
      ))}
    </>
  );
}

interface TurnIconProps {
  maneuver: string;
  color: string;
}

function TurnIcon({ maneuver, color }: TurnIconProps) {
  const size = 16;

  switch (maneuver) {
    case 'turn-left':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <path d="M14 6v3.59L9.41 5 8 6.41l6 6V16h2V6z" />
        </svg>
      );
    case 'turn-right':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <path d="M10 6v3.59L14.59 5 16 6.41l-6 6V16H8V6z" />
        </svg>
      );
    case 'straight':
    default:
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
        </svg>
      );
  }
}

export default NavigationRouteLayer;
